import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaService } from '../../../quotas/services/quota.service';
import { ApplicationItem } from '../../../quotas/models/application.interface';

export interface HistoricalVolumesDialogData {
  application: ApplicationItem;
  supplierId: string;
  quotaYear: number;
}

@Component({
  selector: 'app-historical-volumes-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './historical-volumes-dialog.component.html',
  styleUrls: ['./historical-volumes-dialog.component.css']
})
export class HistoricalVolumesDialogComponent {
  private quotaService = inject(QuotaService);
  private dialogRef = inject(MatDialogRef<HistoricalVolumesDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  data = inject<HistoricalVolumesDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');

  // Поля в тоннах
  volumeFirstYearTons: number | null = null;
  volumeNineMonthsPriorTons: number | null = null;
  totalImportVolumeTons: number | null = null;
  comment = '';

  // Конвертация тонн в кг
  private tonsToKg(tons: number | null): number | null {
    if (tons == null) return null;
    return Math.round(tons * 1000);
  }

  // Авто-расчет суммы
  calculateTotal(): void {
    const v1 = this.volumeFirstYearTons ?? 0;
    const v2 = this.volumeNineMonthsPriorTons ?? 0;

    if (this.volumeFirstYearTons != null || this.volumeNineMonthsPriorTons != null) {
      this.totalImportVolumeTons = v1 + v2;
    } else {
      this.totalImportVolumeTons = null;
    }
  }

  isFormValid(): boolean {
    return !!(
      this.volumeFirstYearTons != null &&
      this.volumeNineMonthsPriorTons != null &&
      this.comment
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.loading.set(true);
    this.errorMessage.set('');

    const meatType = this.data.application.meatType;

    // Конвертируем тонны в кг
    const v1kg = this.tonsToKg(this.volumeFirstYearTons);
    const v2kg = this.tonsToKg(this.volumeNineMonthsPriorTons);
    const totalKg = (v1kg ?? 0) + (v2kg ?? 0);
    const totalOrNull = (v1kg == null && v2kg == null) ? null : totalKg;

    this.quotaService.createHistoricalVolumes({
      comment: this.comment || '',
      meatType: {
        id: meatType.id,
        nameRu: meatType.nameRu,
        nameKy: meatType.nameKy || '',
        alias: meatType.alias || ''
      },
      quotaYear: this.data.quotaYear - 1, // год - 1
      supplierId: this.data.supplierId,
      totalImportVolume: totalOrNull!,
      volumeFirstYear: v1kg!,
      volumeNineMonthsPrior: v2kg!
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open('Исторические поставки успешно сохранены', 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.errorMessage.set(response.msg || 'Ошибка сохранения');
        }
      },
      error: (error) => {
        console.error('Error creating historical volumes:', error);
        this.errorMessage.set('Ошибка сохранения');
        this.loading.set(false);
      }
    });
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
